import React, { useEffect, useState } from "react";
import { Plus, Trash2, Eye, X, Save, Users, Download, BarChart3 } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

const blankForm = {
    question: "",
    description: "",
    options: [
        { label: "" },
        { label: "" },
    ],
    show_results: "live",
    active: true,
    starts_at: "",
    ends_at: "",
};

const SHOW_RESULTS_OPTIONS = [
    { value: "live", label: "Live (visitors see counts immediately)" },
    { value: "after_close", label: "After close / after voting" },
    { value: "admin_only", label: "Admin only" },
];

const PollsPanel = () => {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [adding, setAdding] = useState(false);
    const [form, setForm] = useState(blankForm);
    const [voters, setVoters] = useState(null); // { poll, list }
    const [voterLoading, setVoterLoading] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/polls");
            setRows(data || []);
        } catch (e) {
            toast.error("Could not load polls");
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, []);

    const setOption = (idx, value) => {
        const opts = [...form.options];
        opts[idx] = { label: value };
        setForm({ ...form, options: opts });
    };

    const addOption = () => {
        if (form.options.length >= 12) return;
        setForm({ ...form, options: [...form.options, { label: "" }] });
    };

    const removeOption = (idx) => {
        if (form.options.length <= 2) return;
        const opts = form.options.filter((_, i) => i !== idx);
        setForm({ ...form, options: opts });
    };

    const create = async () => {
        const question = form.question.trim();
        const options = form.options.filter((o) => o.label.trim());
        if (!question || options.length < 2) {
            toast.error("Question + at least 2 non-empty options required");
            return;
        }
        try {
            await api.post("/admin/polls", {
                question,
                description: form.description.trim(),
                options: options.map((o) => ({ label: o.label.trim() })),
                show_results: form.show_results,
                active: form.active,
                starts_at: form.starts_at || null,
                ends_at: form.ends_at || null,
            });
            toast.success("Poll created");
            setAdding(false);
            setForm(blankForm);
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Create failed");
        }
    };

    const toggleActive = async (p) => {
        try {
            await api.patch(`/admin/polls/${p.id}`, { active: !p.active });
            await load();
        } catch (e) {
            toast.error("Update failed");
        }
    };

    const remove = async (p) => {
        if (!window.confirm(`Delete poll "${p.question}" and all its votes?`)) return;
        try {
            await api.delete(`/admin/polls/${p.id}`);
            toast.success("Poll deleted");
            await load();
        } catch (e) {
            toast.error("Delete failed");
        }
    };

    const openVoters = async (p) => {
        setVoterLoading(true);
        setVoters({ poll: p, list: [], total: 0 });
        try {
            const { data } = await api.get(`/admin/polls/${p.id}/voters`);
            setVoters({ poll: p, list: data.voters || [], total: data.total_votes || 0 });
        } catch (e) {
            toast.error("Could not load voters");
            setVoters(null);
        } finally {
            setVoterLoading(false);
        }
    };

    const exportCsv = () => {
        if (!voters?.list?.length) return;
        const header = ["voted_at", "voter_name", "voter_email", "voter_phone", "option_label", "ip_address"];
        const lines = [header.join(",")];
        voters.list.forEach((v) => {
            lines.push(header.map((k) => `"${(v[k] || "").toString().replace(/"/g, '""')}"`).join(","));
        });
        const blob = new Blob([lines.join("\n")], { type: "text/csv" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `poll-${voters.poll.id}-voters.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    return (
        <div className="space-y-6" data-testid="polls-panel">
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
                <div>
                    <h2 className="font-heading text-2xl text-[#1C252E] tracking-tight">Voting polls</h2>
                    <p className="text-sm text-[#4A5568] mt-1">
                        Quick public polls. Voter name + email (and optional phone) are <strong>admin-only</strong> — the public view shows nothing but tallies.
                    </p>
                </div>
                <button
                    onClick={() => { setAdding(true); setForm(blankForm); }}
                    data-testid="poll-add-btn"
                    className="inline-flex items-center gap-2 bg-[#E53935] hover:bg-[#D32F2F] text-white rounded-full px-5 py-2.5 text-sm font-medium transition"
                >
                    <Plus className="w-4 h-4" strokeWidth={2} /> New poll
                </button>
            </div>

            {adding && (
                <div className="bg-white border border-[#E53935]/30 rounded-2xl p-5 space-y-3" data-testid="poll-form">
                    <div>
                        <label className="text-xs uppercase tracking-wider font-bold text-[#4A5568]">Question</label>
                        <input
                            data-testid="poll-question-input"
                            value={form.question}
                            onChange={(e) => setForm({ ...form, question: e.target.value })}
                            maxLength={500}
                            className="mt-1 w-full rounded-xl border border-black/10 px-4 py-2.5 bg-white focus:outline-none focus:ring-2 focus:ring-[#E53935]/40 focus:border-[#E53935]"
                            placeholder="What should we name the new gym?"
                        />
                    </div>
                    <div>
                        <label className="text-xs uppercase tracking-wider font-bold text-[#4A5568]">Description (optional)</label>
                        <textarea
                            data-testid="poll-description-input"
                            rows={2}
                            value={form.description}
                            onChange={(e) => setForm({ ...form, description: e.target.value })}
                            className="mt-1 w-full rounded-xl border border-black/10 px-4 py-2.5 bg-white focus:outline-none focus:ring-2 focus:ring-[#E53935]/40 focus:border-[#E53935]"
                        />
                    </div>
                    <div>
                        <label className="text-xs uppercase tracking-wider font-bold text-[#4A5568]">Options (2–12)</label>
                        <div className="mt-1 space-y-2">
                            {form.options.map((o, idx) => (
                                <div key={idx} className="flex items-center gap-2">
                                    <input
                                        data-testid={`poll-option-${idx}-input`}
                                        value={o.label}
                                        onChange={(e) => setOption(idx, e.target.value)}
                                        maxLength={200}
                                        className="flex-1 rounded-xl border border-black/10 px-4 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-[#E53935]/40"
                                        placeholder={`Option ${idx + 1}`}
                                    />
                                    {form.options.length > 2 && (
                                        <button onClick={() => removeOption(idx)} className="p-2 rounded-full hover:bg-red-50 text-[#E53935]" aria-label="Remove option">
                                            <X className="w-4 h-4" strokeWidth={2} />
                                        </button>
                                    )}
                                </div>
                            ))}
                            {form.options.length < 12 && (
                                <button
                                    type="button"
                                    onClick={addOption}
                                    data-testid="poll-add-option-btn"
                                    className="inline-flex items-center gap-1 text-sm text-[#E53935] hover:underline"
                                >
                                    <Plus className="w-3.5 h-3.5" strokeWidth={2} /> Add option
                                </button>
                            )}
                        </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        <div>
                            <label className="text-xs uppercase tracking-wider font-bold text-[#4A5568]">Results visibility</label>
                            <select
                                data-testid="poll-show-results-select"
                                value={form.show_results}
                                onChange={(e) => setForm({ ...form, show_results: e.target.value })}
                                className="mt-1 w-full rounded-xl border border-black/10 px-4 py-2.5 bg-white focus:outline-none focus:ring-2 focus:ring-[#E53935]/40"
                            >
                                {SHOW_RESULTS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                            </select>
                        </div>
                        <div>
                            <label className="text-xs uppercase tracking-wider font-bold text-[#4A5568]">Starts at (optional)</label>
                            <input
                                type="datetime-local"
                                data-testid="poll-starts-at-input"
                                value={form.starts_at}
                                onChange={(e) => setForm({ ...form, starts_at: e.target.value ? new Date(e.target.value).toISOString() : "" })}
                                className="mt-1 w-full rounded-xl border border-black/10 px-4 py-2.5 bg-white focus:outline-none focus:ring-2 focus:ring-[#E53935]/40"
                            />
                        </div>
                        <div>
                            <label className="text-xs uppercase tracking-wider font-bold text-[#4A5568]">Ends at (optional)</label>
                            <input
                                type="datetime-local"
                                data-testid="poll-ends-at-input"
                                value={form.ends_at}
                                onChange={(e) => setForm({ ...form, ends_at: e.target.value ? new Date(e.target.value).toISOString() : "" })}
                                className="mt-1 w-full rounded-xl border border-black/10 px-4 py-2.5 bg-white focus:outline-none focus:ring-2 focus:ring-[#E53935]/40"
                            />
                        </div>
                    </div>
                    <label className="flex items-center gap-2 text-sm text-[#1C252E]">
                        <input
                            type="checkbox"
                            data-testid="poll-active-checkbox"
                            checked={form.active}
                            onChange={(e) => setForm({ ...form, active: e.target.checked })}
                        /> Publish immediately (active)
                    </label>
                    <div className="flex items-center justify-end gap-2 pt-2">
                        <button onClick={() => { setAdding(false); setForm(blankForm); }} className="text-sm text-[#4A5568] hover:text-[#1C252E] inline-flex items-center gap-1">
                            <X className="w-4 h-4" strokeWidth={2} /> Cancel
                        </button>
                        <button onClick={create} data-testid="poll-save-btn" className="inline-flex items-center gap-1 bg-[#E53935] hover:bg-[#D32F2F] text-white rounded-full px-5 py-2 text-sm font-medium">
                            <Save className="w-4 h-4" strokeWidth={2} /> Create poll
                        </button>
                    </div>
                </div>
            )}

            {loading ? (
                <p className="text-sm text-[#4A5568]">Loading…</p>
            ) : rows.length === 0 ? (
                <p className="text-sm text-[#4A5568] italic" data-testid="polls-empty">No polls yet. Click <strong>New poll</strong> to create one.</p>
            ) : (
                <ul className="space-y-3" data-testid="polls-list">
                    {rows.map((p) => (
                        <li key={p.id} data-testid={`poll-row-${p.id}`} className="bg-white border border-black/10 rounded-2xl p-4">
                            <div className="flex items-start justify-between gap-3 flex-wrap">
                                <div className="min-w-0 flex-1">
                                    <p className="font-medium text-[#1C252E]">{p.question}</p>
                                    {p.description && <p className="text-xs text-[#4A5568] mt-1">{p.description}</p>}
                                    <div className="flex items-center gap-2 mt-1 flex-wrap text-[10px] uppercase tracking-wider font-bold">
                                        <span className={`px-2 py-0.5 rounded-full ${p.is_open ? "bg-green-100 text-green-800" : "bg-[#F4F1EA] text-[#4A5568]"}`}>
                                            {p.is_open ? "Open" : "Closed"}
                                        </span>
                                        <span className="px-2 py-0.5 rounded-full bg-[#F4F1EA] text-[#4A5568]">
                                            {p.show_results.replace("_", " ")}
                                        </span>
                                        <span className="text-[10px] text-[#4A5568]">{p.total_votes ?? 0} votes</span>
                                    </div>
                                </div>
                                <div className="flex items-center gap-1">
                                    <button onClick={() => openVoters(p)} data-testid={`poll-voters-${p.id}`} className="p-1.5 rounded-full text-[#4A5568] hover:bg-[#F4F1EA]" aria-label="View voters">
                                        <Users className="w-4 h-4" strokeWidth={1.5} />
                                    </button>
                                    <button onClick={() => toggleActive(p)} data-testid={`poll-toggle-${p.id}`} className="p-1.5 rounded-full text-[#4A5568] hover:bg-[#F4F1EA]" aria-label="Toggle active">
                                        <Eye className="w-4 h-4" strokeWidth={1.5} />
                                    </button>
                                    <button onClick={() => remove(p)} data-testid={`poll-delete-${p.id}`} className="p-1.5 rounded-full text-[#4A5568] hover:text-[#E53935] hover:bg-[#E53935]/10" aria-label="Delete">
                                        <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                                    </button>
                                </div>
                            </div>
                            <ul className="mt-3 space-y-1.5">
                                {p.options.map((o) => {
                                    const total = p.total_votes || 0;
                                    const pct = total ? Math.round(((o.votes || 0) / total) * 100) : 0;
                                    return (
                                        <li key={o.id} className="flex items-center gap-3 text-sm">
                                            <div className="flex-1 min-w-0">
                                                <div className="flex justify-between gap-2">
                                                    <span className="truncate text-[#1C252E]">{o.label}</span>
                                                    <span className="text-[#4A5568] text-xs whitespace-nowrap">{o.votes || 0} · {pct}%</span>
                                                </div>
                                                <div className="mt-1 h-1.5 bg-[#F4F1EA] rounded-full overflow-hidden">
                                                    <div className="h-full bg-[#E53935]" style={{ width: `${pct}%` }} />
                                                </div>
                                            </div>
                                        </li>
                                    );
                                })}
                            </ul>
                        </li>
                    ))}
                </ul>
            )}

            {voters && (
                <div className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center p-4" data-testid="poll-voters-modal">
                    <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[85vh] flex flex-col">
                        <div className="flex items-center justify-between p-5 border-b border-black/5">
                            <div className="min-w-0">
                                <p className="text-xs uppercase tracking-wider font-bold text-[#E53935] flex items-center gap-1"><BarChart3 className="w-3.5 h-3.5" strokeWidth={2} /> Admin-only voter list</p>
                                <h3 className="font-heading text-lg text-[#1C252E] truncate">{voters.poll.question}</h3>
                                <p className="text-xs text-[#4A5568] mt-0.5">{voters.total} total votes</p>
                            </div>
                            <div className="flex items-center gap-1">
                                <button onClick={exportCsv} data-testid="poll-voters-export" className="p-2 rounded-full hover:bg-[#F4F1EA] text-[#4A5568]" aria-label="Export CSV">
                                    <Download className="w-4 h-4" strokeWidth={1.5} />
                                </button>
                                <button onClick={() => setVoters(null)} className="p-2 rounded-full hover:bg-[#F4F1EA]" aria-label="Close">
                                    <X className="w-4 h-4 text-[#4A5568]" strokeWidth={1.5} />
                                </button>
                            </div>
                        </div>
                        <div className="flex-1 overflow-y-auto p-5">
                            {voterLoading ? (
                                <p className="text-sm text-[#4A5568]">Loading…</p>
                            ) : voters.list.length === 0 ? (
                                <p className="text-sm text-[#4A5568] italic">No votes yet.</p>
                            ) : (
                                <div className="overflow-x-auto">
                                    <table className="w-full text-sm">
                                        <thead className="text-left text-[10px] uppercase tracking-wider font-bold text-[#4A5568]">
                                            <tr>
                                                <th className="py-2 pr-3">When</th>
                                                <th className="py-2 pr-3">Voted for</th>
                                                <th className="py-2 pr-3">Name</th>
                                                <th className="py-2 pr-3">Email</th>
                                                <th className="py-2 pr-3">Phone</th>
                                                <th className="py-2 pr-3">IP</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {voters.list.map((v) => (
                                                <tr key={v.id} className="border-t border-black/5">
                                                    <td className="py-2 pr-3 text-xs text-[#4A5568] whitespace-nowrap">{(v.voted_at || "").slice(0, 16).replace("T", " ")}</td>
                                                    <td className="py-2 pr-3 text-[#1C252E]">{v.option_label}</td>
                                                    <td className="py-2 pr-3 text-[#1C252E]">{v.voter_name}</td>
                                                    <td className="py-2 pr-3 text-[#4A5568]">{v.voter_email}</td>
                                                    <td className="py-2 pr-3 text-[#4A5568]">{v.voter_phone || "—"}</td>
                                                    <td className="py-2 pr-3 text-[10px] text-[#4A5568] font-mono">{v.ip_address || ""}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PollsPanel;
